import { NgRedux } from '@angular-redux/store';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    Area,
    DataTransformService,
    IntercityStop,
    MatrixProductPrice,
    Medium,
    Product,
    ProductCategory,
    RelatedCard,
    Ticket,
    TicketMedium,
    Translatable,
    UidService
} from '@ridango/core';
import Env from '@ridango/core/env';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { AccountTicketsService } from '../account-tickets';
import { TicketActions } from './actions/ticket.actions';
import { TicketsState } from './store/tickets.store';
import { PurchaseChannel } from '@ridango/core/lib/enums/purchase-channel.enum';
import { getPurchaseChannel } from '@ridango/core/lib/utils/channel.util';
import { TranslateService } from '@ngx-translate/core';
import { ModalController } from '@ionic/angular';

export const CardTypeMasks = {
    UID: 0, // 1
    PAN_HASH: 1, // 2
    UTN: 2, // 4
    PERSON_ID: 3, // 8
    BARCODE: 4, // 16
    GLOBAL: 5, // 32
    QR: 6, // 64
    RKF: 7, // 128- RKF card type ('R' )
    RDC: 8, // 256 Flygbussarna Reservation Data Code follows ( Base64 encoded ) ( 'A' )
    MTB: 9, // 512  - MTB card type( 'M' )
    Foreign: 10, // 1024 -  - Foreign card type( 'F' )
    UTN2: 11 // 2048( 'T' )
};

@Injectable({
    providedIn: 'root'
})
export class TicketsService {
    constructor(
        private ngRedux: NgRedux<TicketsState>,
        private actions: TicketActions,
        private dataTransformer: DataTransformService,
        private uid: UidService,
        @Inject('ENV') private env: Env,
        private http: HttpClient,
        private modalCtrl: ModalController,
        private accountTicketsService: AccountTicketsService,
        private translate: TranslateService
    ) {}

    createTicket(ticket: Ticket = {}): string {
        if (ticket.doc_num) {
            this.ngRedux.dispatch(this.actions.createTicket(ticket));
            return ticket.doc_num + '';
        }
        ticket.uid = this.uid.generate();
        this.ngRedux.dispatch(this.actions.createTicket(ticket));
        return ticket.uid;
    }

    fetchExtraZones(origin, destination): Observable<Zone[]> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/zones/origin/${origin}/destination/${destination}/extra_zones`;
        return this.http.get<Zone[]>(url);
    }

    fetchAllStationsExtraZones(origin, destination): Observable<Zone[]> {
        const url = `${this.env.CORE_API_URL}/partner/v2/service/zones/origin/${origin}/destination/${destination}/all_stations`;
        return this.http.get<Zone[]>(url);
    }

    setStartingDate(uid: string, date: Date) {
        this.ngRedux.dispatch(this.actions.setStartingDate({ uid: uid, date: date }));
    }

    setEndingDate(uid: string, date: Date) {
        this.ngRedux.dispatch(this.actions.setEndingDate({ uid: uid, date: date }));
    }

    unsetDates(uid: string) {
        this.ngRedux.dispatch(this.actions.unsetTicketDates({ uid: uid }));
    }

    setProduct(uid: string, product: Product, productPrice: MatrixProductPrice = null) {
        this.ngRedux.dispatch(this.actions.setProduct({ uid: uid, product: product, productPrice: productPrice }));
    }

    setOriginStop(uid: string, stop: IntercityStop) {
        this.ngRedux.dispatch(this.actions.setOriginStop({ uid: uid, stop: stop }));
    }

    setDestinationStop(uid: string, stop: IntercityStop) {
        this.ngRedux.dispatch(this.actions.setDestinationStop({ uid: uid, stop: stop }));
    }

    clearAllLocalTickets() {
        this.ngRedux.dispatch(this.actions.clearAllTickets());
    }

    removeLocalTicket(ticket: Ticket) {
        this.ngRedux.dispatch(this.actions.removeTicket(ticket));
    }

    setRenewTicket(ticket) {
        this.ngRedux.dispatch(this.actions.setRenewTicket(ticket));
    }

    removeRenewTicket() {
        this.ngRedux.dispatch(this.actions.removeRenewTicket());
    }

    setArea(uid: string, area: Area) {
        this.ngRedux.dispatch(this.actions.setArea(uid, area));
    }

    setTravellerCategory(uid: string, category: ProductCategory) {
        this.ngRedux.dispatch(this.actions.setTravellerCategory(uid, category));
    }

    setProductCategory(uid: string, category: ProductCategory) {
        this.ngRedux.dispatch(this.actions.setProductCategory(uid, category));
    }

    setMedium(uid: string, medium: Medium) {
        this.ngRedux.dispatch(this.actions.setMedium(uid, medium));
    }

    setRelatedCard(uid: string, card: RelatedCard) {
        this.ngRedux.dispatch(this.actions.setRelatedCard(uid, card));
    }

    setPrice(uid: string, price: number) {
        this.ngRedux.dispatch(this.actions.setTicketPrice({ uid: uid, price: price }));
    }

    fetchTicketsByRelatedCard(card: RelatedCard, companyId = null): Observable<Ticket[]> {
        let url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/tickets/related-card/${card.id}`;

        if (companyId && parseInt(companyId, 10)) {
            url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/${card.id}/company/${companyId}/tickets`;
        }

        return this.http.get(url).pipe(
            map((d: Ticket[]) => {
                return d.map((t: Ticket) => {
                    if (!t.product) {
                        t.product = { translations: [] };
                    } else if (!t.product.translations) {
                        t.product.translations = [];
                    }
                    t.product.name = t.name;
                    t.product.translations = t.product.translations.concat(
                        this.dataTransformer.transformObjectTranslations(t, 'name')
                    );
                    if (t.trip_messages) {
                        t.trip_messages.forEach((m) => {
                            m.translations = [];
                            m.translations = this.dataTransformer.transformObjectTranslations(m, 'message');
                        });
                    }
                    return t;
                });
            })
        );
    }

    fetchActiveTickets(params?: { includeTicketsExpiredInGivenHours?: number }): Observable<Ticket[]> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/tickets/active`;

        return this.http
            .get(url, {
                params: {
                    ...(this.env.FILTER_PURCHASES_BY_CHANNEL && { channel: this.accountTicketsService.getChannel() }),
                    ...(params || {})
                }
            })
            .pipe(
                map((d: Ticket[]) => {
                    return d.map((t: Ticket) => {
                        if (t.trip_messages) {
                            t.trip_messages.forEach((m) => {
                                m.translations = [];
                                m.translations = this.dataTransformer.transformObjectTranslations(m, 'message');
                            });
                        }
                        return t;
                    });
                })
            );
    }

    setRelatedCardTickets(card: RelatedCard, tickets: Ticket[]) {
        this.ngRedux.dispatch(this.actions.setRelatedCardTickets(card, tickets));
    }

    setTravelCardsToTicket(cardIds, ticketUid) {
        this.ngRedux.dispatch(this.actions.setTravelCardsToTicket(cardIds, ticketUid));
    }

    setMediumsToTicket(mediumNums, ticketUid) {
        this.ngRedux.dispatch(this.actions.setMediumsToTicket(mediumNums, ticketUid));
    }

    setEmailsToTicket(emails, ticketUid) {
        this.ngRedux.dispatch(this.actions.setEmailsToTicket(emails, ticketUid));
    }

    setCorporatePrice(ticketUid) {
        this.ngRedux.dispatch(this.actions.setCorporatePrice(ticketUid));
    }

    toggleAllRelatedCards(travelCardsIds, ticketUid) {
        this.ngRedux.dispatch(this.actions.toggleAllRelatedCards(travelCardsIds, ticketUid));
    }

    toggleSelectedRelatedCard(travelCardId, ticketUid) {
        this.ngRedux.dispatch(this.actions.toggleSelectedRelatedCard(travelCardId, ticketUid));
    }

    setEmailTicket(ticket) {
        this.ngRedux.dispatch(this.actions.setEmailTicket(ticket));
    }

    clearEmailTickets() {
        this.ngRedux.dispatch(this.actions.clearEmailTickets());
    }

    setVoucherTicket(ticket) {
        this.ngRedux.dispatch(this.actions.setVoucherTicket(ticket));
    }

    setVoucherSubTicket(ticket) {
        this.ngRedux.dispatch(this.actions.setVoucherSubTicket(ticket));
    }

    setSelectedVoucher(ticket) {
        this.ngRedux.dispatch(this.actions.setSelectedVoucherTicket(ticket));
    }

    clearVouchers() {
        this.ngRedux.dispatch(this.actions.clearVouchers());
    }

    setAmountOfTickets(uid, amount) {
        this.ngRedux.dispatch(this.actions.setAmountOfTickets(uid, amount));
    }

    pauseTicket(docNum) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/tickets/${docNum}/pause`;

        return this.http.put(url, null);
    }

    reactivateTicket(docNum) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/tickets/${docNum}/resume`;

        return this.http.put(url, null);
    }

    setRenewableTicket(ticket) {
        this.ngRedux.dispatch(this.actions.setRenewableTicket(ticket));
    }

    setRefundTicket(ticket) {
        this.ngRedux.dispatch(this.actions.setRefundTicket(ticket));
    }

    forgetRenewableTicket() {
        this.ngRedux.dispatch(this.actions.setRenewableTicket(null));
    }

    setAvailableMediumTypes(types) {
        this.ngRedux.dispatch(this.actions.setAvailableMediumTypes(types));
    }

    async determineMediumTypesByMask(mask): Promise<TicketMedium[]> {
        return new Promise((resolve, _reject) => {
            const masks = [];

            // tslint:disable-next-line: no-bitwise
            if ((mask & (1 << CardTypeMasks.UTN)) !== 0) {
                masks.push(new TicketMedium('U', 'LOCAL_LABEL_BUY_TICKETS_UTN_TRAVELCARD'));
            }

            // tslint:disable-next-line: no-bitwise
            if ((mask & (1 << CardTypeMasks.UTN2)) !== 0) {
                masks.push(new TicketMedium('T', 'LOCAL_LABEL_BUY_TICKETS_UTN2_TRAVELCARD'));
            }

            // tslint:disable-next-line: no-bitwise
            if ((mask & (1 << CardTypeMasks.QR)) !== 0) {
                masks.push(new TicketMedium('Q', 'LOCAL_LABEL_BUY_TICKETS_EMAIL'));
            }

            resolve(masks);
        });
    }

    isValidated(ticket): boolean {
        const starting = new Date(ticket.starting);
        const now = new Date();

        return (
            ticket.starting != null &&
            starting < now &&
            (ticket.initial_units != null ||
                ticket.initial_units !== ticket.units ||
                (ticket.ending != null && ticket.initial_units == null && ticket.units == null))
        );
    }

    getRecentTickets(customerAccountId?: number | string) {
        const channel: PurchaseChannel = getPurchaseChannel(this.env);
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/ticket/recent`;
        const requestParams: {
            [param: string]: string | number | boolean | readonly (string | number | boolean)[];
        } = {
            channel: [channel]
        };

        if (customerAccountId != null) {
            requestParams['customerAccountId'] = customerAccountId;
        }
        return this.http
            .get(url, {
                params: requestParams
            })
            .pipe(shareReplay({ bufferSize: 1, refCount: true }));
    }

    removeHistoryTicket(docNum: string) {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/ticket/history/${docNum}`;
        return this.http.delete(url);
    }

    getSelectedTravellerTypesOnTicket(ticketCompanionProfiles, ticketDynamicProductParameters): string {
        const selectedTicketTypes = [];
        for (const companionProfile of ticketCompanionProfiles) {
            const profileName = (companionProfile.userProfile.nameTranslations as Translatable).translations[
                this.translate.currentLang
            ];

            const profileCode = companionProfile.dynamicProductParameters[0].code;
            const profileAmount = ticketDynamicProductParameters[profileCode];
            if (profileAmount && Number(profileAmount) > 0) {
                selectedTicketTypes.push(`${profileAmount}x ${profileName}`);
            }
        }
        return selectedTicketTypes.join(', ');
    }
}

interface Zone {
    id: number;
    name: string;
}
